import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { api, formatApiError, fileUrl } from '@/lib/api';
import { CategoryBadge } from '@/components/CategoryBadge';
import { Plus, Search, Edit2, Trash2, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useCountUp } from '@/hooks/useCountUp';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function AdminDashboard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');

    const fetchRows = async (query = '') => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/recipients', { params: { q: query } });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => fetchRows(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    const handleDelete = async (id) => {
        try {
            await api.delete(`/admin/recipients/${id}`);
            toast.success('Recipient deleted');
            fetchRows(q);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="min-h-screen bg-black">
            <Navbar variant="admin" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.28em] text-un-lime font-semibold">Admin</div>
                        <h1 className="mt-2 font-display font-black uppercase tracking-tight text-white text-4xl md:text-5xl">
                            Recipients
                        </h1>
                        <p className="mt-2 text-white/60 font-body">Manage your UnCloned<span className="text-un-berry">®</span> Brand Award recipient list.</p>
                    </div>
                    <Link
                        to="/admin/recipients/new"
                        data-testid="admin-new-recipient-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-un-lime text-black font-semibold px-5 py-3 hover:bg-white transition-colors"
                    >
                        <Plus size={16} /> Add recipient
                    </Link>
                </div>

                <div className="relative mb-6">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                    <input
                        data-testid="admin-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, company, or email"
                        className="w-full rounded-full bg-neutral-950 border border-white/15 pl-12 pr-5 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
                    />
                </div>

                <div className="rounded-3xl border border-white/10 overflow-hidden bg-neutral-950">
                    {loading ? (
                        <div className="p-16 flex items-center justify-center text-white/60">
                            <Loader2 className="animate-spin" size={20} />
                            <span className="ml-3 uppercase tracking-[0.2em] text-xs">Loading</span>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-16 text-center text-white/60">
                            <div className="font-display text-2xl text-white mb-2">No recipients yet</div>
                            <div className="text-sm mb-6">Add your first recipient to get started.</div>
                            <Link
                                to="/admin/recipients/new"
                                className="inline-flex items-center gap-2 rounded-full bg-un-lime text-black font-semibold px-5 py-3"
                            >
                                <Plus size={16} /> Add recipient
                            </Link>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/10">
                            {rows.map((r) => (
                                <div
                                    key={r.id}
                                    data-testid={`admin-row-${r.slug}`}
                                    className="p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:bg-white/[0.02] transition-colors"
                                >
                                    <div className="h-14 w-14 rounded-full overflow-hidden bg-neutral-800 border border-white/10 shrink-0">
                                        {r.photoUrl ? (
                                            <img src={fileUrl(r.photoUrl)} alt="" className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="h-full w-full flex items-center justify-center text-white/50 font-display font-bold">
                                                {r.firstName?.[0]}{r.lastName?.[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="text-white font-semibold truncate">
                                                {r.firstName} {r.lastName}
                                            </div>
                                        </div>
                                        <div className="text-sm text-white/60 truncate">
                                            {r.companyName || '—'} · {r.email || 'no email'}
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {(r.awards || []).map((a) => (
                                                <CategoryBadge key={a} name={a} size="sm" />
                                            ))}
                                        {/* Total milestones badge with count-up */}
                                        <CountBadge total={r.totalMilestones ?? 0} slug={r.slug} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 self-end sm:self-center">
                                    <a
                                        href={`/recipients/${r.slug}`}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        data-testid={`admin-preview-${r.slug}`}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-white/80 hover:border-un-lime hover:text-un-lime px-3 py-2 text-xs transition-colors"
                                        title="Open public profile in new tab"
                                    >
                                        <Eye size={14} /> Preview
                                    </a>
                                    <Link
                                            to={`/admin/recipients/${r.id}/edit`}
                                            data-testid={`admin-edit-${r.slug}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-white/80 hover:border-un-water hover:text-un-water px-3 py-2 text-xs transition-colors"
                                        >
                                            <Edit2 size={14} /> Edit
                                        </Link>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <button
                                                    data-testid={`admin-delete-${r.slug}`}
                                                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-white/80 hover:border-red-500 hover:text-red-400 px-3 py-2 text-xs transition-colors"
                                                >
                                                    <Trash2 size={14} /> Delete
                                                </button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent className="bg-neutral-950 text-white border-white/15">
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle className="font-display uppercase tracking-tight">
                                                        Delete {r.firstName} {r.lastName}?
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription className="text-white/60">
                                                        This will permanently remove them from the public directory.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel data-testid="delete-cancel-btn" className="bg-transparent border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        data-testid="delete-confirm-btn"
                                                        onClick={() => handleDelete(r.id)}
                                                        className="bg-red-500 text-white hover:bg-red-600"
                                                    >
                                                        Delete
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CountBadge({ total, slug }) {
    const value = useCountUp(total, { duration: 1000, trigger: slug });
    return (
        <span
            data-testid={`admin-milestones-${slug}`}
            className="text-[10px] uppercase tracking-[0.2em] text-white/50 border border-white/10 rounded-full px-2 py-0.5 tabular-nums"
        >
            {value} milestones
        </span>
    );
}
